import {
  Box,
  CircularProgress,
  Grid,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Dns, Language, Logout, Settings } from '@mui/icons-material';
import { CustomerSelector } from './components/CustomerSelector';
import { SVG } from 'components/Asset/SVG';
import { CustomLoadingButton } from 'components/Buttons/CustomLoadingButton/CustomLoadingButton';
import { CustomMuiThemeProvider } from 'theme/CustomMuiThemeProvider';
import Permission from 'components/Permission/Permission';
import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppDispatch, useAppSelector } from 'store/hook';
import CustomerHandler from 'handlers/customer.handler';
import { type Customer } from 'types/customer.types';
import {
  getAppLogo,
  CLIENT_THEME,
  clientThemePalette,
} from 'utils/clientTheme';
import useAppTheme from 'hooks/useAppTheme';
import { useTranslation } from 'react-i18next';
import { setLanguage } from 'store/language.slice';
import type { SupportedLanguage } from 'i18n';

const LANGUAGE_OPTIONS: Array<{ value: SupportedLanguage; label: string }> = [
  { value: 'en', label: 'English' },
  { value: 'ru', label: 'Русский' },
  { value: 'es', label: 'Español' },
  // { value: 'fr', label: 'Français' }, // disabled for now — not needed yet
];

export interface CustomerSelectorOption {
  id: string;
  display: string;
  code: string;
  unitSystem: string;
  timezone: string;
  mineType: string;
  proj4String: string | null;
  localOriginEasting: number | null;
  localOriginNorthing: number | null;
  modules?: {
    isPartsModule: boolean;
    isTiresModule: boolean;
    isMachinesModule: boolean;
  };
}

/**
 *
 * @returns  JSX.Element
 *
 * This component is used to display the customer selection page.
 * It is only visible to users with the director or internal-user role.
 * It is the first page that is displayed after login if no customer is selected
 * It is also displayed when the user clicks on the change link in the top bar
 */
export const CustomerSelectionView = (): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isAuthenticated } = useAuth0();
  const accessToken = useAppSelector((state) => state.authReducer.accessToken);
  const role = useAppSelector((state) => state.authReducer.role);
  const language = useAppSelector((state) => state.languageReducer.language);
  const customerHandler = new CustomerHandler();
  const { enableDarkTheme } = useAppTheme();

  const handleLanguageChange = (e: SelectChangeEvent) => {
    dispatch(setLanguage(e.target.value as SupportedLanguage));
  };
  const colorMode = enableDarkTheme ? 'dark' : 'light';
  const themeColors = clientThemePalette[CLIENT_THEME][colorMode];
  const [isCustomerLoading, setIsCustomerLoading] = useState<boolean>(true);

  const [selectorOptions, setSelectorOptions] = useState<
    CustomerSelectorOption[]
  >([]);

  const isUserInfoLoading = useAppSelector(
    (state) => state.authReducer.isUserInfoLoading
  );

  const handleCustomerSettings = () => {
    navigate('/customer-settings');
  };

  const handleNavigationConfig = () => {
    navigate('/nav-config');
  };

  const handleLogout = () => {
    // Instead of immediately clearing session and removing auth,
    // navigate to the logging out screen where those operations will be performed
    navigate('/logging-out');
  };

  const fetchCustomers = async () => {
    try {
      const customers: Customer[] = await customerHandler.getAll(false);

      if (Array.isArray(customers) && customers.length) {
        const options = customers
          .map((customer) => ({
            id: customer.customerId,
            display: customer.customerName,
            code: customer.customerCode,
            unitSystem: customer.unitSystem,
            timezone: customer.timeZone ?? 'UTC',
            mineType: customer.mineType ?? '',
            proj4String: customer.proj4String ?? null,
            localOriginEasting: customer.localOriginEasting ?? null,
            localOriginNorthing: customer.localOriginNorthing ?? null,
            modules: customer.modules ?? {
              isPartsModule: false,
              isTiresModule: customer.isTiresModule ?? false,
              isMachinesModule: false,
            },
          }))
          .sort((firstOption, secondOption) =>
            firstOption.display.localeCompare(secondOption.display, undefined, {
              sensitivity: 'base',
            })
          );
        setSelectorOptions(options);
      } else {
        throw new Error('INvalid customers data ');
      }
    } catch (error) {
      console.error('Failed to fetch customer selector options', error);
      setSelectorOptions([]);
    } finally {
      setIsCustomerLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated || !accessToken || !role) return;
    void fetchCustomers();
  }, [role, isAuthenticated, accessToken]);

  return (
    <CustomMuiThemeProvider>
      <Box sx={{ minHeight: '100vh', width: '100vw', display: 'flex' }}>
        <Grid container sx={{ minHeight: '100vh' }}>
          <Grid
            item
            xs={12}
            md={5}
            sx={{
              backgroundColor: themeColors.navbarBg,
              color: 'white',
              p: 6,
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              overflow: 'hidden',
              justifyContent: 'space-between',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: '-10%',
                left: '-10%',
                width: '400px',
                height: '400px',
                borderRadius: '50%',
                backgroundColor: themeColors.primary,
                filter: 'blur(100px)',
                opacity: 0.2,
                pointerEvents: 'none',
              }}
            />

            <Box sx={{ zIndex: 10 }}>
              {(() => {
                const logo = getAppLogo();
                return logo.type === 'img' ? (
                  <img
                    src={logo.src}
                    alt={logo.alt}
                    style={{
                      width: '250px',
                      height: '100px',
                      objectFit: 'contain',
                    }}
                  />
                ) : (
                  <SVG
                    style={{ width: '250px', height: '100px' }}
                    name={logo.svgName}
                  />
                );
              })()}
            </Box>

            <Box sx={{ zIndex: 10, mb: 4 }}>
              <Typography variant="h3" sx={{ fontWeight: 300, mb: 2 }}>
                {t('welcome_to')} <br />
                <Box
                  component="span"
                  sx={{ fontWeight: 700, color: themeColors.primary }}
                >
                  {CLIENT_THEME === 'yokohama' ? 'Yokohama TPMS' : 'X.Platform'}
                </Box>
              </Typography>
            </Box>

            <Typography
              variant="caption"
              sx={{ color: 'rgba(255,255,255,0.4)', zIndex: 10 }}
            >
              © {new Date().getFullYear()}{' '}
              {CLIENT_THEME === 'yokohama' ? 'Yokohama TPMS' : 'SYMX.AI'}{' '}
              {t('all_rights_reserved')}
            </Typography>
          </Grid>

          <Grid
            item
            xs={12}
            md={7}
            sx={{
              backgroundColor: '#FFFFFF',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              p: 4,
            }}
          >
            <Box
              sx={{ position: 'absolute', top: 24, right: 24 }}
              display={'flex'}
              alignItems={'center'}
              gap={1}
            >
              <Select
                size="small"
                value={language}
                onChange={handleLanguageChange}
                startAdornment={
                  <Language
                    sx={{ fontSize: '1rem', mr: 0.5, color: 'primary.main' }}
                  />
                }
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: 'primary.main',
                  background: 'transparent',
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '& .MuiSelect-select': {
                    py: '6px',
                    pl: '4px',
                    pr: '24px !important',
                  },
                  '& .MuiSvgIcon-root.MuiSelect-icon': {
                    color: 'primary.main',
                  },
                  '&:hover': {
                    background: 'rgba(99,102,241,0.06)',
                    borderRadius: 1,
                  },
                }}
              >
                {LANGUAGE_OPTIONS.map(({ value, label }) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
              <Permission roles={['director']}>
                <CustomLoadingButton
                  startIcon={<Dns />}
                  onClick={handleNavigationConfig}
                  variant="text"
                >
                  {t('navigation_config')}
                </CustomLoadingButton>
              </Permission>
              <Permission roles={['director', 'external-director']}>
                <CustomLoadingButton
                  startIcon={<Settings />}
                  onClick={handleCustomerSettings}
                  variant="text"
                >
                  {t('customer_settings')}
                </CustomLoadingButton>
              </Permission>
              <CustomLoadingButton
                onClick={handleLogout}
                startIcon={<Logout fontSize="small" />}
                color="error"
                variant="text"
              >
                {t('logout')}
              </CustomLoadingButton>
            </Box>

            <Box sx={{ width: '100%', maxWidth: '400px' }}>
              <Box sx={{ mb: 1 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 500,
                    color: '#1A1A1A',
                    mb: 1,
                    fontSize: '1.9rem',
                  }}
                >
                  {t('select_site')}
                </Typography>
                <Typography variant="body2" sx={{ color: '#8A92A0' }}>
                  {t('please_identify_the_site_you_wish_to_manage')}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {isCustomerLoading || isUserInfoLoading ? (
                  <Box display="flex" justifyContent="center">
                    <CircularProgress color="primary" />
                  </Box>
                ) : selectorOptions.length === 0 ? (
                  <Typography
                    variant="body2"
                    sx={{
                      color: themeColors.grey,
                      textAlign: 'center',
                      mt: 2,
                    }}
                  >
                    {t('no_sites_accounts_are_associated_with_your_user')}
                    <br />
                    {t('please_contact_an_administrator_for_access')}
                  </Typography>
                ) : (
                  <CustomerSelector selectorOptions={selectorOptions} />
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </CustomMuiThemeProvider>
  );
};
